import { useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Select,
  TextInput,
  Button,
  FileInput,
  Badge,
  ActionIcon,
  Paper,
  Box,
} from '@mantine/core';
import {
  IconUpload,
  IconFile,
  IconTrash,
  IconX,
  IconCamera,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ResourceInput } from '@medplum/react';
import { ConsentData, ConsentSignerType } from '@/types/consent.types';
import { DocumentReference, RelatedPerson } from '@medplum/fhirtypes';
import { RELATIONSHIP_CODE_MAPPINGS } from '@consts/consent.consts';
import { EXTERNAL_CS, HL7, SYSTEMS } from '@/consts/systems.consts';
import { useDateDisplay } from '@hooks/useDateDisplay';
import ConfirmCloseDialog from '@/components/ConfirmCloseDialog';
import { useConsentOperations } from '@/hooks/useConsentOperations';

interface ConsentSignerSectionProps {
  consentData: ConsentData;
  updateConsentData: (updates: Partial<ConsentData>) => void;
  linkedDocuments: DocumentReference[];
  documentsToDelete: string[];
  setDocumentsToDelete: (documents: string[]) => void;
  isReadOnly: boolean;
  patient?: any;
  onScanClick: () => void;
}

export function ConsentSignerSection({
  consentData,
  updateConsentData,
  linkedDocuments,
  documentsToDelete,
  setDocumentsToDelete,
  isReadOnly,
  patient,
  onScanClick,
}: ConsentSignerSectionProps) {
  const { t } = useTranslation(['lims', 'common']);
  const formatDate = useDateDisplay();
  const [showNewRelatedPersonForm, setShowNewRelatedPersonForm] =
    useState(false);

  // Use consent operations hook for duplicate detection
  const {
    showDuplicateWarning,
    duplicateRelatedPerson,
    setShowDuplicateWarning,
    setDuplicateRelatedPerson,
  } = useConsentOperations({
    consentData,
    patient,
    isReadOnly,
  });

  const handleSignerTypeChange = (value: string | null) => {
    updateConsentData({
      signerType: value as ConsentSignerType,
      // Clear related person data when changing type
      selectedRelatedPerson: undefined,
      newRelatedPersonName: undefined,
      newRelatedPersonRelationship: undefined,
      authorizationDocument: undefined,
      existingRelatedPersonIdToDelete: undefined,
    });
    // Reset form visibility
    setShowNewRelatedPersonForm(false);
  };

  const handleRelatedPersonChange = (
    relatedPerson: RelatedPerson | undefined
  ) => {
    updateConsentData({
      selectedRelatedPerson: relatedPerson,
      newRelatedPersonName: undefined,
      newRelatedPersonRelationship: undefined,
      existingRelatedPersonIdToDelete: undefined, // Clear any pending deletion
    });
    // Hide form when selecting existing person
    setShowNewRelatedPersonForm(false);
  };

  const handleAuthorizationDocumentChange = (file: File | null) => {
    updateConsentData({ authorizationDocument: file || undefined });
  };

  const handleDeleteAuthorizationDocument = (documentId: string) => {
    setDocumentsToDelete([...documentsToDelete, documentId]);
  };

  const isAuthorizedPerson =
    consentData.selectedRelatedPerson?.relationship?.[0]?.coding?.[0]?.code ===
      HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.POWER_OF_ATTORNEY ||
    consentData.newRelatedPersonRelationship ===
      HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.POWER_OF_ATTORNEY;
  const existingAuthDocs = linkedDocuments.filter(
    (doc) =>
      doc.type?.coding?.[0]?.code ===
        EXTERNAL_CS.LOINC.CODES.AUTHORIZATION_DOCUMENT &&
      doc.type?.coding?.[0]?.system === SYSTEMS.LOINC &&
      !documentsToDelete.includes(doc.id || '')
  );

  return (
    <Stack gap="sm">
      <Select
        label={t('consent.signer.title', { ns: 'lims' })}
        required
        placeholder={t('consent.signer.selectPlaceholder', { ns: 'lims' })}
        value={consentData.signerType}
        onChange={handleSignerTypeChange}
        disabled={isReadOnly}
        data={[
          {
            value: 'patient',
            label: t('consent.signer.patient', { ns: 'lims' }),
          },
          {
            value: 'other',
            label: t('consent.signer.other', { ns: 'lims' }),
          },
        ]}
      />

      {/* Related Person Selection for non-patient signers */}
      {consentData.signerType === 'other' && (
        <Stack gap="sm">
          <Group justify="space-between" grow>
            <ResourceInput
              label={t('consent.signer.selectRelatedPerson', { ns: 'lims' })}
              resourceType="RelatedPerson"
              searchCriteria={{
                patient: `Patient/${patient?.id}`,
              }}
              name="relatedPerson"
              placeholder={t('consent.signer.searchRelatedPerson', {
                ns: 'lims',
              })}
              defaultValue={consentData.selectedRelatedPerson}
              onChange={handleRelatedPersonChange}
              disabled={isReadOnly}
            />

            {/* Display selected RelatedPerson info */}
            {consentData.selectedRelatedPerson && (
              <Paper p="sm" bg="blue.0" radius="sm" mt="lg">
                <Text size="xs" c="dimmed">
                  {t(
                    `consent.relationships.${
                      consentData.selectedRelatedPerson.relationship?.[0]
                        ?.coding?.[0]?.display ||
                      consentData.selectedRelatedPerson.relationship?.[0]
                        ?.coding?.[0]?.code
                    }`,
                    '',
                    { ns: 'lims' }
                  ) || 'Unknown Relationship'}
                </Text>
              </Paper>
            )}
          </Group>

          {/* Button to show new related person form */}
          {!showNewRelatedPersonForm && !consentData.selectedRelatedPerson && (
            <Button
              variant="light"
              size="sm"
              onClick={() => setShowNewRelatedPersonForm(true)}
              disabled={isReadOnly}
            >
              {t('consent.signer.addNewRelatedPerson', { ns: 'lims' })}
            </Button>
          )}

          {/* New related person form */}
          {showNewRelatedPersonForm && (
            <Paper radius="sm" p="sm" bg="gray.0">
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>
                  {t('consent.signer.createNewRelatedPerson', { ns: 'lims' })}
                </Text>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="gray"
                  onClick={() => {
                    setShowNewRelatedPersonForm(false);
                    updateConsentData({
                      newRelatedPersonName: undefined,
                      newRelatedPersonRelationship: undefined,
                    });
                  }}
                  disabled={isReadOnly}
                >
                  <IconX size={14} />
                </ActionIcon>
              </Group>

              <Group gap="sm" grow>
                <TextInput
                  label={t('consent.signer.nameLabel', { ns: 'lims' })}
                  placeholder={t('consent.signer.namePlaceholder', {
                    ns: 'lims',
                  })}
                  value={consentData.newRelatedPersonName || ''}
                  onChange={(e) =>
                    updateConsentData({
                      newRelatedPersonName: e.currentTarget.value,
                      selectedRelatedPerson: undefined, // Clear selection when typing
                      existingRelatedPersonIdToDelete: undefined, // Clear deletion flag when name changes
                    })
                  }
                  disabled={isReadOnly}
                />

                <Select
                  label={t('consent.signer.relationshipLabel', { ns: 'lims' })}
                  placeholder={t('consent.signer.relationshipPlaceholder', {
                    ns: 'lims',
                  })}
                  value={consentData.newRelatedPersonRelationship || null}
                  onChange={(value) =>
                    updateConsentData({
                      newRelatedPersonRelationship: value || undefined,
                      selectedRelatedPerson: undefined, // Clear selection when changing
                      existingRelatedPersonIdToDelete: undefined, // Clear deletion flag when relationship changes
                    })
                  }
                  disabled={isReadOnly}
                  data={Object.entries(RELATIONSHIP_CODE_MAPPINGS).map(
                    ([code, mapping]) => {
                      // Map display text to translation keys
                      const translationKeyMap: Record<string, string> = {
                        mother: 'consent.relationships.mother',
                        father: 'consent.relationships.father',
                        guardian: 'consent.relationships.guardian',
                        'power of attorney':
                          'consent.relationships.powerOfAttorney',
                      };

                      const translationKey =
                        translationKeyMap[mapping.display.toLowerCase()];

                      return {
                        value: code,
                        label: translationKey
                          ? t(translationKey, {
                              ns: 'lims',
                              defaultValue: mapping.display,
                            })
                          : mapping.display,
                      };
                    }
                  )}
                />
              </Group>
            </Paper>
          )}

          {/* Authorization document upload for authorized persons */}
          {isAuthorizedPerson && (
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                {t('consent.signer.authorizationDocument', { ns: 'lims' })}
                <Text component="span" c="red" ml="4px">
                  *
                </Text>
              </Text>

              {/* Show existing authorization documents if they exist */}
              {existingAuthDocs.length > 0 && (
                <Stack gap="xs" mt="-4px">
                  {existingAuthDocs.map((doc, index) => (
                    <Box key={index} p="xs" bg="gray.0">
                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconFile size={16} />
                          <Text size="sm">
                            {doc.content?.[0]?.attachment?.title ||
                              doc.description ||
                              `Authorization Document ${index + 1}`}
                          </Text>
                          {doc.date && (
                            <Badge size="xs" variant="light">
                              {formatDate(doc.date)}
                            </Badge>
                          )}
                        </Group>
                        {!isReadOnly && doc.id && (
                          <ActionIcon
                            size="sm"
                            color="red"
                            variant="subtle"
                            onClick={() =>
                              handleDeleteAuthorizationDocument(doc.id || '')
                            }
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Box>
                  ))}
                </Stack>
              )}

              {/* Only show file input if no authorization document exists or all are marked for deletion */}
              {existingAuthDocs.length === 0 && (
                <Group gap="sm" grow>
                  <Button
                    variant="light"
                    leftSection={<IconCamera size={16} />}
                    onClick={onScanClick}
                    disabled={isReadOnly}
                  >
                    {t('documentScanner.title', {
                      ns: 'common',
                    })}
                  </Button>
                  <FileInput
                    placeholder={t('consent.signer.uploadAuthorization', {
                      ns: 'lims',
                    })}
                    leftSection={<IconUpload size={16} />}
                    accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                    value={consentData.authorizationDocument}
                    onChange={handleAuthorizationDocumentChange}
                    disabled={isReadOnly}
                  />
                </Group>
              )}
            </Stack>
          )}
        </Stack>
      )}

      {/* Duplicate RelatedPerson Warning Modal */}
      <ConfirmCloseDialog
        opened={showDuplicateWarning}
        onCancel={() => {
          setShowDuplicateWarning(false);
          setDuplicateRelatedPerson(null);
          updateConsentData({
            selectedRelatedPerson: undefined,
            newRelatedPersonName: undefined,
            newRelatedPersonRelationship: undefined,
            existingRelatedPersonIdToDelete: undefined,
          });
        }}
        onConfirm={() => {
          if (duplicateRelatedPerson?.id) {
            updateConsentData({
              existingRelatedPersonIdToDelete: duplicateRelatedPerson.id,
            });
          }
          setShowDuplicateWarning(false);
          setDuplicateRelatedPerson(null);
        }}
        title={t('consent.signer.duplicateWarning.title', '', { ns: 'lims' })}
        message={t('consent.signer.duplicateWarning.message', '', {
          ns: 'lims',
          relationship:
            duplicateRelatedPerson?.relationship?.[0]?.coding?.[0]?.code ===
            HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.MOTHER
              ? t('consent.relationships.mother', '', {
                  ns: 'lims',
                }).toLowerCase()
              : t('consent.relationships.father', '', {
                  ns: 'lims',
                }).toLowerCase(),
          name: duplicateRelatedPerson?.name?.[0]?.text || '',
        })}
        confirmLabel={t('consent.signer.duplicateWarning.confirmLabel', '', {
          ns: 'lims',
        })}
        cancelLabel={t('consent.signer.duplicateWarning.cancelLabel', '', {
          ns: 'lims',
        })}
        confirmColor="orange"
        size="md"
      />
    </Stack>
  );
}
